import * as React from 'react';
import PropTypes from 'prop-types';

export const ScrollContext = React.createContext();

export const ScrollProvider = ({ children }) => {
  const scrollContainerRef = React.useRef();

  const [atTopOfPage, setAtTopOfPage] = React.useState(true);

  // Scrollbars: .container.firstChild. Restoration mode: ref is the scroll element itself.
  const getScrollView = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return null;
    if (container.container?.firstChild) {
      return container.container.firstChild;
    }
    if (typeof HTMLElement !== 'undefined' && container instanceof HTMLElement) {
      return container;
    }
    return null;
  }, []);

  const scrollToTop = React.useCallback(() => {
    const view = getScrollView();
    if (view) {
      view.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [getScrollView]);

  /** Smooth-scroll CustomScrollbars view to an element id (hash links jump with no animation). */
  const scrollToId = React.useCallback((id, { offset = 96 } = {}) => {
    const view = getScrollView();
    const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
    if (!view || !el) return;

    const scrollMarginTop = typeof window !== 'undefined'
      ? parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0
      : 0;
    const viewRect = view.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = Math.max(0, view.scrollTop + (elRect.top - viewRect.top) - offset - scrollMarginTop);

    view.scrollTo({ top, behavior: 'smooth' });
  }, [getScrollView]);

  const updateScrollLocation = React.useCallback((restorationRef) => {
    const view = restorationRef?.current || getScrollView();
    if (!view) return;

    if (view.scrollTop === 0) {
      if (!atTopOfPage) {
        setAtTopOfPage(true);
      }
    } else if (atTopOfPage) {
      setAtTopOfPage(false);
    }
  }, [getScrollView, atTopOfPage]);

  const value = React.useMemo(() => ({
    scrollContainerRef,
    updateScrollLocation,
    scrollToTop,
    scrollToId,
    atTopOfPage,
  }), [atTopOfPage, scrollToTop, scrollToId, updateScrollLocation]);

  return (
    <ScrollContext.Provider value={value}>
      {children}
    </ScrollContext.Provider>
  );
};

ScrollProvider.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.object.isRequired
};